import { z } from 'zod/v3';
import {
  ControlEvidenceStatusEnumSchema,
  type ControlEvidenceStatusEnum,
  EvidenceReviewStatusSchema,
  ShareAccessTypeSchema,
  TemplateTypeSchema,
  AppTypeSchema,
  PublisherTypeSchema,
  LicenseTypeSchema,
  VerificationBadgeSchema,
  SubtaskSourceSchema,
} from '../generated/enums';
import { TemplateSamplingConfigSchema } from './population.dto';
import { LabelDTOSchema } from './label.dto';

// ===== Validation Type Names (predefined options for users) =====
export const ValidationTypeNameSchema = z.enum([
  // Screenshot validation types
  'access_denied',
  'successful_access',
  'user_role_visible',
  'audit_trail',
  'configuration_screen',
  'error_message',
  'approval_workflow',
  'user_management',
  'data_classification',
  'encryption_indicator',
  'backup_confirmation',
  'mfa_setup',
  // Text validation types
  'clear_conclusion',
  'exception_documented',
  'timestamp_present',
  'tester_identified',
  // URL validation types
  'production_url',
  'secure_url',
  // Generic
  'present',
  'custom',
]);

export type ValidationTypeName = z.infer<typeof ValidationTypeNameSchema>;

// ===== RFC-103: Pagination Configuration Schemas =====

/** URL-based pagination configuration */
export const PaginationUrlConfigSchema = z.object({
  /** URL pattern with {page} placeholder, e.g., "https://app.com/users?page={page}" */
  pattern: z.string(),
  /** Starting page number (usually 1, sometimes 0) */
  startPage: z.number().default(1),
});

export type PaginationUrlConfig = z.infer<typeof PaginationUrlConfigSchema>;

/** Selector-based pagination configuration */
export const PaginationSelectorConfigSchema = z.object({
  /** CSS selector for the "next" button */
  nextSelector: z.string(),
  /** Time to wait after clicking next (ms), default 1500 */
  waitAfterClick: z.number().default(1500),
});

export type PaginationSelectorConfig = z.infer<typeof PaginationSelectorConfigSchema>;

/** Sample strategy configuration */
export const PaginationSampleConfigSchema = z.object({
  /** Hard limit on total pages to capture, default 10 */
  maxPages: z.number().default(10),
  /** Number of first pages to capture, default 2 */
  captureFirst: z.number().default(2),
  /** Number of last pages to capture, default 2 */
  captureLast: z.number().default(2),
  /** Number of evenly-spaced middle pages to sample, default 3 */
  sampleMiddle: z.number().default(3),
});

export type PaginationSampleConfig = z.infer<typeof PaginationSampleConfigSchema>;

/** First/Last strategy configuration */
export const PaginationFirstLastConfigSchema = z.object({
  /** Number of first pages to capture, default 1 */
  captureFirst: z.number().default(1),
  /** Number of last pages to capture, default 1 */
  captureLast: z.number().default(1),
  /** Safety cap on max pages, default 10 */
  maxPages: z.number().default(10),
});

export type PaginationFirstLastConfig = z.infer<typeof PaginationFirstLastConfigSchema>;

/** All pages strategy configuration */
export const PaginationAllConfigSchema = z.object({
  /** Safety cap on max pages, default 20 */
  maxPages: z.number().default(20),
});

export type PaginationAllConfig = z.infer<typeof PaginationAllConfigSchema>;

/** Step pagination configuration (RFC-103) */
export const StepPaginationConfigSchema = z.object({
  enabled: z.boolean(),
  /** Navigation method: 'url' for direct URL navigation, 'selector' for clicking next button */
  method: z.enum(['url', 'selector']),
  /** URL-based pagination configuration (preferred when available) */
  urlConfig: PaginationUrlConfigSchema.optional(),
  /** Selector-based pagination configuration (fallback) */
  selectorConfig: PaginationSelectorConfigSchema.optional(),
  /** Capture strategy */
  strategy: z.enum(['sample', 'firstLast', 'all']),
  /** Strategy-specific configuration for 'sample' strategy */
  sampleConfig: PaginationSampleConfigSchema.optional(),
  /** Strategy-specific configuration for 'firstLast' strategy */
  firstLastConfig: PaginationFirstLastConfigSchema.optional(),
  /** Strategy-specific configuration for 'all' strategy */
  allConfig: PaginationAllConfigSchema.optional(),
});

export type StepPaginationConfig = z.infer<typeof StepPaginationConfigSchema>;

// ===== Template Workflow Step (for executionConfig) =====
export const TemplateWorkflowStepSchema = z.object({
  id: z.string(),
  /** Step action type */
  action: z.enum(['navigate', 'click', 'type', 'extract', 'wait', 'scroll', 'hover', 'select', 'upload', 'download', 'screenshot']),
  /** URL for navigation steps */
  url: z.string().optional(),
  /** Human-readable description of the step */
  description: z.string().optional(),
  /** Whether to capture a screenshot after this step */
  screenshot: z.boolean().optional(),
  /** Selector for interactive steps */
  selector: z.string().optional(),
  /** Value for type/select actions */
  value: z.string().optional(),
  /** RFC-103: Pagination configuration for this step */
  pagination: StepPaginationConfigSchema.optional(),
});

export type TemplateWorkflowStep = z.infer<typeof TemplateWorkflowStepSchema>;

// ===== Template Execution Config (stored in GuidedTemplate.executionConfig) =====
// Import workflow schemas and types for proper typing and validation
import type { WorkflowStepDTO, WorkflowParamDTO, WorkflowOutputDTO } from './workflow.dto';
import { WorkflowStepSchema, WorkflowParamSchema, WorkflowOutputSchema } from './workflow.dto';

export const TemplateExecutionConfigSchema = z.object({
  /** Target URL for static screenshot tests */
  targetUrl: z.string().optional(),
  /** RFC-103: Pagination configuration for static screenshot tests */
  pagination: StepPaginationConfigSchema.optional(),
  /** Workflow steps to execute (WorkflowStepDTO[]) */
  steps: z.array(WorkflowStepSchema).optional(),
  /** Workflow params (Record<string, WorkflowParamDTO>) */
  params: z.record(WorkflowParamSchema).optional(),
  /** Workflow-level success criteria */
  successCriteria: z.string().optional(),
  /** Expected outputs from workflow execution */
  outputs: z.array(WorkflowOutputSchema).optional(),
  /** Whether execution is automated */
  automated: z.boolean().optional(),
  /** Browser profile requirements */
  browserProfile: z.object({
    required: z.boolean().optional(),
    loginUrl: z.string().optional(),
  }).optional(),
  // RFC-104: POLICY type fields
  /** Policy type identifier (for POLICY templates) */
  policyType: z.string().optional(),
  /** Whether policy requires acknowledgment (for POLICY templates) */
  requiresAcknowledgment: z.boolean().optional(),
});

// Provide strongly-typed interface for better type safety
export interface TemplateExecutionConfig {
  targetUrl?: string;
  pagination?: StepPaginationConfig;
  steps?: WorkflowStepDTO[];
  params?: Record<string, WorkflowParamDTO>;
  successCriteria?: string;
  outputs?: WorkflowOutputDTO[];
  automated?: boolean;
  browserProfile?: {
    required?: boolean;
    loginUrl?: string;
  };
  policyType?: string;
  requiresAcknowledgment?: boolean;
}

// Import PopulationType and SamplingMethod from generated enums (RFC-104)
import { PopulationTypeSchema, SamplingMethodSchema } from '../generated/enums';
export type { PopulationType, SamplingMethod } from '../generated/enums';

// ===== Simplified Element (what users configure) =====
// type defines WHAT to validate in the evidence, not HOW evidence is collected.
// Collection method is determined by templateType (POLICY=upload, GUIDED=recording).
export const SimplifiedElementSchema = z.object({
  id: z.string(),
  // What type of content to validate in the evidence
  // - screenshot: Validate image content (UI screenshots, configuration screens)
  // - text: Validate text content (conclusions, explanations, policy excerpts)
  // - url: Validate URL presence (links, references)
  // - metadata: System-captured data (auto-validated)
  // Note: File upload is implicit for POLICY templates - elements validate document CONTENT
  type: z.enum(['screenshot', 'text', 'url', 'metadata']),
  name: z.string(),
  description: z.string(),
  validationType: ValidationTypeNameSchema,
  customPrompt: z.string().optional(),
  weight: z.number().min(0).max(100),
  required: z.boolean(),
  minCount: z.number().optional(),
});

export type SimplifiedElement = z.infer<typeof SimplifiedElementSchema>;

// ===== Quality Thresholds =====
export const QualityThresholdsSchema = z.object({
  minimum: z.number().min(0).max(100),
  acceptable: z.number().min(0).max(100),
  excellent: z.number().min(0).max(100),
});

export type QualityThresholds = z.infer<typeof QualityThresholdsSchema>;

// ===== LLM Generation Schemas (with descriptions for prompts) =====
// These schemas are used by AI agents (sop-parser, control-matrix-converter) to generate templates

/**
 * Simplified sampling config for LLM generation
 * Has descriptions that help the LLM understand each field
 */
export const LLMSamplingConfigSchema = z.object({
  requiresPopulation: z.boolean().describe(
    'True if the control requires sampling from a population (e.g., "review a sample of terminated users")'
  ),
  populationType: PopulationTypeSchema.optional().describe(
    'Type of population to sample from'
  ),
  populationFields: z.array(z.string()).optional().describe(
    'Fields to collect for each population item, only if populationType is CUSTOM'
  ),
  minimumSampleSizeRecommended: z.number().optional().describe(
    'Recommended minimum sample size (e.g., 5, 10, 25)'
  ),
  recommendedMethod: SamplingMethodSchema.optional().describe(
    'How to select items from the population'
  ),
  sharedEvidenceSupported: z.boolean().optional().describe(
    'True = one screenshot can show all samples (e.g., filtered list view). False = separate evidence per sample item.'
  ),
});

export type LLMSamplingConfig = z.infer<typeof LLMSamplingConfigSchema>;

/**
 * Subtask definition for LLM generation
 * Instructions are required since testers rely on them for guidance
 */
export const LLMSubtaskDefinitionSchema = z.object({
  id: z.string().describe('Unique identifier (kebab-case, e.g., "vendor-inventory")'),
  name: z.string().describe('Display name for the subtask'),
  description: z.string().describe(
    'Brief summary (1 sentence) of WHAT evidence this subtask collects. Shown in subtask listings.'
  ),
  type: TemplateTypeSchema.describe('Type of evidence: POLICY, GUIDED, STATIC_SCREENSHOT, etc.'),
  order: z.number().describe('Display order (0-indexed)'),
  scoringCriteria: z.object({
    instructions: z.string().describe(
      'Detailed markdown guidance on HOW to collect this subtask evidence. Required - testers rely on this.'
    ),
    requiredElements: z.array(SimplifiedElementSchema).describe('Evidence items to validate for this subtask'),
    qualityThresholds: QualityThresholdsSchema,
  }),
});

export type LLMSubtaskDefinition = z.infer<typeof LLMSubtaskDefinitionSchema>;

/**
 * Main schema for LLM-generated templates
 * Used by sop-parser and control-matrix-converter
 */
export const LLMGeneratedTemplateSchema = z.object({
  title: z.string().describe('Clear, actionable test name'),
  description: z.string().describe(
    'Brief summary (1-2 sentences) of WHAT this test verifies. Shown in listings and audit reports.'
  ),
  templateType: TemplateTypeSchema,
  appType: AppTypeSchema.nullish().optional().describe('Application type if identifiable'),
  instructions: z.string().describe(
    'Detailed markdown guidance on HOW to collect evidence. Required - testers rely on this for guidance.'
  ),
  requiredElements: z.array(SimplifiedElementSchema).describe('Evidence items to validate'),
  qualityThresholds: QualityThresholdsSchema,
  samplingConfig: LLMSamplingConfigSchema.nullable().describe('Population sampling config if applicable'),
  suggestedFrequency: z.enum(['ONCE', 'MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL']).optional(),
  subtasks: z.array(LLMSubtaskDefinitionSchema).optional().nullable().describe(
    'For templates requiring multiple distinct types of evidence. Set to null/empty for single-type templates.'
  ),
});

export type LLMGeneratedTemplate = z.infer<typeof LLMGeneratedTemplateSchema>;

// ===== RFC-113: Subtask Definition (for multi-requirement templates) =====
/**
 * Defines a subtask within a template. Used when a single control requires
 * multiple types of evidence (e.g., Policy + Screenshot + System List).
 */
export const SubtaskDefinitionSchema = z.object({
  /** Unique identifier for this subtask within the template */
  id: z.string(),
  /** Display name for the subtask (e.g., "Vendor Inventory") */
  name: z.string().min(1).max(200),
  /** Description of what evidence should be provided */
  description: z.string(),
  /** Type of evidence this subtask collects */
  type: TemplateTypeSchema,
  /** Display order (0-indexed) */
  order: z.number().int().min(0),
  /** Per-subtask scoring criteria */
  scoringCriteria: z.object({
    /** Instructions specific to this subtask */
    instructions: z.string().optional(),
    /** Required elements for this subtask */
    requiredElements: z.array(SimplifiedElementSchema),
    /** Quality thresholds for this subtask */
    qualityThresholds: QualityThresholdsSchema,
  }),
  /** Optional: execution config for WORKFLOW/STATIC_SCREENSHOT subtasks */
  executionConfig: TemplateExecutionConfigSchema.optional(),
});

export type SubtaskDefinition = z.infer<typeof SubtaskDefinitionSchema>;

// SubtaskSource is exported from generated/enums, no need to re-export

// ===== Scoring Criteria (stored in GuidedTemplate.scoringCriteria) =====
export const ScoringCriteriaSchema = z.object({
  // Workflow instructions (markdown) - shown to users and AI
  // Users can write ordered lists, tips, warnings, etc.
  // Template authors can address both human testers and AI in one field
  instructions: z.string().optional(),

  // What to validate (concept-based)
  // For single-requirement templates, this is the main validation list
  // For multi-requirement templates (with subtasks), this can be empty or contain parent-level criteria
  requiredElements: z.array(SimplifiedElementSchema),

  // Quality thresholds
  qualityThresholds: QualityThresholdsSchema,

  // RFC-113: Subtask definitions (when template has multiple requirements)
  // If present and non-empty, this template creates subtasks when instantiated
  subtasks: z.array(SubtaskDefinitionSchema).optional(),
});

export type ScoringCriteria = z.infer<typeof ScoringCriteriaSchema>;

// ===== Compliance Framework DTOs =====
export const ComplianceFrameworkDefDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string().nullable(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  /** Whether the current user can manage (edit/delete) this framework */
  canManage: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ComplianceFrameworkDefDTO = z.infer<typeof ComplianceFrameworkDefDTOSchema>;

export const CreateComplianceFrameworkDefDTOSchema = z.object({
  name: z.string().min(1).max(100),
});

export type CreateComplianceFrameworkDefDTO = z.infer<typeof CreateComplianceFrameworkDefDTOSchema>;

// ===== Control Definition DTOs =====
export const ControlDefDTOSchema = z.object({
  id: z.string(),
  frameworkId: z.string(),
  workspaceId: z.string().nullable(),
  controlId: z.string(),
  title: z.string(),
  description: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ControlDefDTO = z.infer<typeof ControlDefDTOSchema>;

// ===== Template Control Mapping DTOs (many-to-many) =====
export const GuidedTemplateControlMappingDTOSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  controlDefId: z.string(),
  isPrimary: z.boolean(),
  displayOrder: z.number(),
  createdAt: z.date(),
  // Optional nested control info
  controlDef: z.object({
    id: z.string(),
    controlId: z.string(),
    title: z.string(),
    frameworkId: z.string(),
  }).optional(),
});

export type GuidedTemplateControlMappingDTO = z.infer<typeof GuidedTemplateControlMappingDTOSchema>;

export const CreateControlDefDTOSchema = z.object({
  frameworkId: z.string(),
  controlId: z.string().min(1).max(50),
  title: z.string().min(1).max(200),
  description: z.string().min(1),
});

export type CreateControlDefDTO = z.infer<typeof CreateControlDefDTOSchema>;

export const UpdateControlDefDTOSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
});

export type UpdateControlDefDTO = z.infer<typeof UpdateControlDefDTOSchema>;

// ===== Guided Template DTOs =====
export const GuidedTemplateDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  frameworkId: z.string(),
  isPublic: z.boolean(),
  usageCount: z.number(),
  scoringCriteria: ScoringCriteriaSchema,
  currentVersion: z.number(), // Template version number
  author: z.string().nullable(),
  license: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),

  // Display fields (can be derived from doc.topic)
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  taskNumber: z.number(),

  // Control mappings (many-to-many: which controls this template satisfies)
  controlMappings: z.array(GuidedTemplateControlMappingDTOSchema).optional(),

  // ===== RFC-101: Template Marketplace Fields =====
  templateType: TemplateTypeSchema.optional(),
  appType: AppTypeSchema.nullable().optional(),
  executionConfig: TemplateExecutionConfigSchema.nullable().optional(), // RFC-103: typed execution config
  samplingConfig: TemplateSamplingConfigSchema.nullable().optional(), // RFC-104: population sampling config

  // Publishing & Attribution
  publisherType: PublisherTypeSchema.optional(),
  publisherUserId: z.string().nullable().optional(),
  publisherTitle: z.string().nullable().optional(),
  publisherOrg: z.string().nullable().optional(),
  publisherOrgId: z.string().nullable().optional(),
  licenseType: LicenseTypeSchema.optional(),

  // Verification
  verifierOrgId: z.string().nullable().optional(),
  verifiedAt: z.date().nullable().optional(),
  verificationBadge: VerificationBadgeSchema.nullable().optional(),

  // Stats
  successRate: z.number().nullable().optional(),
  totalExecutions: z.number().optional(),

  // Clone tracking
  sourceTemplateId: z.string().nullable().optional(),
  clonedByUserId: z.string().nullable().optional(),

  // Optional nested objects for list views
  framework: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    workspaceId: z.string().nullable(),
  }).optional(),
  doc: z.object({
    id: z.string(),
    topic: z.object({
      id: z.string(),
      title: z.string(),
    }),
  }).optional(),

  // Optional publisher user info (for display)
  publisherUser: z.object({
    id: z.string(),
    name: z.string().nullable(),
    image: z.string().nullable(),
  }).nullable().optional(),

  // Optional publisher organization info (for display)
  publisherOrganization: z.object({
    id: z.string(),
    name: z.string(),
    logo: z.string().nullable(),
  }).nullable().optional(),

  // Optional verifier organization info (for display)
  verifierOrg: z.object({
    id: z.string(),
    name: z.string(),
    logo: z.string().nullable(),
  }).nullable().optional(),

  // RFC-104: Current version ID for import
  // This is the ID of the GuidedTemplateVersion record (not the version number)
  currentVersionId: z.string().nullable().optional(),

  // ===== RFC-091 Extension: Partner Template Sharing =====
  /** Visibility scope: PUBLIC | ORGANIZATION | PARTNER_CLIENTS | WORKSPACE */
  visibility: z.enum(['PUBLIC', 'ORGANIZATION', 'PARTNER_CLIENTS', 'WORKSPACE']).optional(),
  /** Partner organization ID (if visibility=PARTNER_CLIENTS) */
  partnerOrgId: z.string().nullable().optional(),
  /** Client org IDs this is shared with (empty = all clients) */
  sharedWithClientIds: z.array(z.string()).optional(),
  /** Whether clients can clone this template */
  clientsCanClone: z.boolean().optional(),
  /** Whether clients can see the source doc content */
  clientsCanViewSource: z.boolean().optional(),
  /** Partner organization info (for display in client view) */
  partnerOrg: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable().optional(),
});

export type GuidedTemplateDTO = z.infer<typeof GuidedTemplateDTOSchema>;

export const CreateGuidedTemplateDTOSchema = z.object({
  docId: z.string(),
  frameworkId: z.string(),
  scoringCriteria: ScoringCriteriaSchema,
  isPublic: z.boolean().default(false),
  license: z.string().optional(),
});

export type CreateGuidedTemplateDTO = z.infer<typeof CreateGuidedTemplateDTOSchema>;

export const UpdateGuidedTemplateDTOSchema = z.object({
  id: z.string(),
  scoringCriteria: ScoringCriteriaSchema.optional(),
  isPublic: z.boolean().optional(),
  license: z.string().optional(),
  executionConfig: TemplateExecutionConfigSchema.optional(), // RFC-103: Execution steps with pagination
});

export type UpdateGuidedTemplateDTO = z.infer<typeof UpdateGuidedTemplateDTOSchema>;

// ===== RFC-110: Evidence Location Schema (inline for ElementScore) =====
// This is a simplified version - full schema is in evidence-viewer.dto.ts
const ElementEvidenceLocationSchema = z.object({
  artifactType: z.enum(['screenshot', 'evidence_file', 'text_response', 'notes']),
  contentType: z.enum(['pdf', 'docx', 'xlsx', 'csv', 'image', 'text', 'json']).optional(),
  screenshotId: z.string().optional(),
  stepId: z.string().optional(),
  evidenceFileId: z.string().optional(),
  chunkId: z.string().optional(),
  page: z.number().optional(),
  section: z.string().optional(),
  sheetName: z.string().optional(),
  columns: z.array(z.string()).optional(),
  rowIndices: z.array(z.number()).optional(),
  cellRange: z.string().optional(),
  textElementId: z.string().optional(),
  noteSnippet: z.string().optional(),
  matchedText: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
});

// ===== Element Score (result of validating one element) =====
export const ElementScoreSchema = z.object({
  elementId: z.string(),
  elementName: z.string(),
  isPresent: z.boolean(),
  isValid: z.boolean(),
  confidence: z.number().min(0).max(100),
  reasoningText: z.string(),
  detectedElements: z.array(z.string()),
  suggestions: z.array(z.string()),
  weightedScore: z.number(),
  // RFC-110: Evidence locations for backlinks
  evidenceLocations: z.array(ElementEvidenceLocationSchema).optional(),
});

export type ElementScore = z.infer<typeof ElementScoreSchema>;

// ===== Evidence Score DTOs =====
export const EvidenceScoreDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  templateId: z.string(),
  completenessScore: z.number(),
  accuracyScore: z.number(),
  clarityScore: z.number(),
  complianceScore: z.number(),
  overallScore: z.number(),
  elementScores: z.array(ElementScoreSchema),
  qualityTier: z.enum(['EXCELLENT', 'ACCEPTABLE', 'NEEDS_IMPROVEMENT', 'FAIL']),
  feedback: z.string().nullable(),
  recommendations: z.array(z.string()).nullable(),
  scoredAt: z.date(),
  scoredBy: z.string().nullable(),
  modelUsed: z.string().nullable(),
});

export type EvidenceScoreDTO = z.infer<typeof EvidenceScoreDTOSchema>;

// Import EvidenceFrequency from generated enums (RFC-104)
import { EvidenceFrequencySchema } from '../generated/enums';
export type { EvidenceFrequency } from '../generated/enums';

// ===== RFC-104: Export Metadata schema =====
export const ExportMetadataSchema = z.object({
  /** What company does (for export only) */
  controlNarrative: z.string().optional(),
  /** Additional auditor notes */
  statusNotes: z.string().optional(),
  /** Future: auditor-defined fields */
  customFields: z.record(z.string()).optional(),
});

export type ExportMetadata = z.infer<typeof ExportMetadataSchema>;

// ===== Control Evidence Status DTOs (RFC-097, RFC-104) =====
export const ControlEvidenceStatusDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  controlDefId: z.string(),
  templateId: z.string().nullable(), // RFC-097: One status per test per control (nullable for migration)
  workflowId: z.string().nullable().optional(), // RFC-104: Workflow for WORKFLOW/STATIC_SCREENSHOT templates
  evidenceDocId: z.string().nullable(),
  status: ControlEvidenceStatusEnumSchema,
  latestScore: z.number().nullable(),
  latestQualityTier: z.string().nullable(),
  assignedTo: z.string().nullable(),
  // Assigned user details (for display)
  assignedUser: z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    image: z.string().nullable(),
  }).nullable().optional(),
  // Work queue fields
  dueDate: z.date().nullable(),
  externalTicketUrl: z.string().nullable(),
  externalTicketId: z.string().nullable(),
  externalTicketSource: z.string().nullable(),
  activeSubmissionId: z.string().nullable(),

  // RFC-104: N/A handling
  naReason: z.string().nullable().optional(),
  naApprovedBy: z.string().nullable().optional(),
  naApprovedAt: z.date().nullable().optional(),

  // RFC-104: Non-performing control handling
  nonPerformingReason: z.string().nullable().optional(),
  designEvidenceDocId: z.string().nullable().optional(),

  // RFC-104: Exception tracking
  hasException: z.boolean().optional(),
  exceptionDescription: z.string().nullable().optional(),
  exceptionApprovedBy: z.string().nullable().optional(),
  exceptionApprovedAt: z.date().nullable().optional(),

  // RFC-104: Evidence scheduling
  frequency: EvidenceFrequencySchema.nullable().optional(),
  nextDueDate: z.date().nullable().optional(),
  reminderSentAt: z.date().nullable().optional(),

  // RFC-104: Export metadata
  exportMetadata: ExportMetadataSchema.nullable().optional(),

  // RFC-104: Population for sampling workflow (can be set before sample is created)
  populationId: z.string().nullable().optional(),
  population: z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    recordCount: z.number(),
    fileUrl: z.string().optional(),
    filename: z.string().optional(),
  }).nullable().optional(),

  // RFC-104: Sample data for SAMPLING tests
  sampleId: z.string().nullable().optional(),
  sample: z.object({
    id: z.string(),
    populationId: z.string(),
    method: z.string(),
    sampleSize: z.number(),
    seed: z.number().nullable(),
    selectedIndices: z.array(z.number()),
    selectedAt: z.date(),
    passedCount: z.number().nullable(),
    failedCount: z.number().nullable(),
    pendingCount: z.number().nullable(),
    population: z.object({
      id: z.string(),
      name: z.string(),
      type: z.string(),
      recordCount: z.number(),
    }).optional(),
    items: z.array(z.object({
      id: z.string(),
      rowIndex: z.number(),
      identifier: z.string(),
      status: z.string(),
      passed: z.boolean().nullable(),
    })).optional(),
  }).nullable().optional(),

  // RFC-107: Subtask support for per-item sampling evidence
  parentId: z.string().nullable().optional(),
  // Parent context (for displaying back link when viewing subtask)
  parent: z.object({
    id: z.string(),
    templateId: z.string().nullable(),
    title: z.string().nullable(),
  }).nullable().optional(),
  sampleItemId: z.string().nullable().optional(),
  // Sample item context (for displaying sample row data when viewing subtask)
  sampleItem: z.object({
    id: z.string(),
    rowIndex: z.number(),
    identifier: z.string(),
    rowData: z.unknown(),
  }).nullable().optional(),
  dynamicParams: z.record(z.unknown()).nullable().optional(),

  // RFC-113: Requirement-based subtask fields
  subtaskDefId: z.string().nullable().optional(),
  subtaskSource: SubtaskSourceSchema.nullable().optional(),
  // RFC-113: Direct title field (for subtasks: from definition or sample item; for tasks: custom override)
  title: z.string().nullable().optional(),
  // Subtask definition details (populated when viewing a subtask created from template definition)
  subtaskDefinition: SubtaskDefinitionSchema.nullable().optional(),

  // Subtask summary (for parent test with subtasks)
  subTaskCount: z.number().optional(),
  subTaskCompletedCount: z.number().optional(),
  hasSubTasks: z.boolean().optional(),

  // Test-level fields (copied from template at creation time, can be edited independently)
  // These are the test's OWN data, not the template's current data
  description: z.string().nullable().optional(),
  appType: z.string().nullable().optional(),
  instructions: z.string().nullable().optional(),
  requiredElements: z.array(SimplifiedElementSchema).nullable().optional(),
  qualityThresholds: QualityThresholdsSchema.nullable().optional(),
  // RFC-113: Subtask definitions for CONTAINER templates
  subtasks: z.array(SubtaskDefinitionSchema).nullable().optional(),
  // Sampling configuration (for population-based tests)
  samplingConfig: TemplateSamplingConfigSchema.nullable().optional(),
  // Execution configuration (for STATIC_SCREENSHOT - targetUrl, pagination, params)
  executionConfig: TemplateExecutionConfigSchema.nullable().optional(),

  // Timestamps
  lastTestedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ControlEvidenceStatusDTO = z.infer<typeof ControlEvidenceStatusDTOSchema>;

export const UpdateControlEvidenceStatusDTOSchema = z.object({
  id: z.string(),
  status: ControlEvidenceStatusEnumSchema.optional(),
  assignedTo: z.string().nullable().optional(),
  dueDate: z.date().nullable().optional(),
  externalTicketUrl: z.string().nullable().optional(),
  externalTicketId: z.string().nullable().optional(),
  externalTicketSource: z.string().nullable().optional(),

  // RFC-104: New updatable fields
  naReason: z.string().nullable().optional(),
  nonPerformingReason: z.string().nullable().optional(),
  designEvidenceDocId: z.string().nullable().optional(),
  hasException: z.boolean().optional(),
  exceptionDescription: z.string().nullable().optional(),
  frequency: EvidenceFrequencySchema.nullable().optional(),
  nextDueDate: z.date().nullable().optional(),
  exportMetadata: ExportMetadataSchema.nullable().optional(),
  // RFC-104: Population link (for sampling workflow)
  populationId: z.string().nullable().optional(),
});

export type UpdateControlEvidenceStatusDTO = z.infer<typeof UpdateControlEvidenceStatusDTOSchema>;

// ===== Additional File (for evidence submissions) =====
export const AdditionalFileDTOSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
  uploadedAt: z.date(),
  mimeType: z.string(),
  size: z.number(), // bytes
});

export type AdditionalFileDTO = z.infer<typeof AdditionalFileDTOSchema>;

// ===== AI Highlight DTOs =====
export const EvidenceHighlightSchema = z.object({
  blockId: z.string(),
  summary: z.string(),
  reason: z.string(),
  confidence: z.number().min(0).max(100),
  elementId: z.string().nullable().optional(), // When template-driven, map back to element
  range: z.object({
    start: z.number(),
    end: z.number(),
    text: z.string(),
  }),
});

export type EvidenceHighlightDTO = z.infer<typeof EvidenceHighlightSchema>;

// ===== Evidence Submission DTOs (RFC-097) =====
export const EvidenceSubmissionDTOSchema = z.object({
  id: z.string(),
  statusId: z.string(),
  docId: z.string().nullable(), // Nullable for subtasks that use EvidenceFile instead
  topicId: z.string().optional(), // Topic ID for navigation
  topicTitle: z.string().optional(), // Topic title for breadcrumb display
  // Template version tracking
  templateVersionId: z.string().nullable(), // Reference to GuidedTemplateVersion
  templateVersion: z.number().nullable(), // Version number (1, 2, 3...)
  templateCurrentVersion: z.number().nullable(), // Current template version for comparison
  submittedBy: z.string().optional(), // Nullable for guest submissions
  submittedByUser: z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    image: z.string().nullable(),
  }).nullable().optional(),
  submittedAt: z.date(),
  revisionNumber: z.number(),
  // Guest submission tracking
  submittedByEmail: z.string().optional(), // Email from share link (for guests)
  shareToken: z.string().optional(), // Share token used to create submission
  // Scores - 4 dimensions
  completenessScore: z.number().nullable(),
  accuracyScore: z.number().nullable(),
  clarityScore: z.number().nullable(),
  complianceScore: z.number().nullable(),
  overallScore: z.number().nullable(),
  qualityTier: z.enum(['EXCELLENT', 'ACCEPTABLE', 'NEEDS_IMPROVEMENT', 'FAIL']).nullable(),
  // Element-by-element scores with LLM reasoning
  elementScores: z.array(ElementScoreSchema).nullable(),
  // AI feedback and recommendations
  feedback: z.string().nullable(),
  recommendations: z.array(z.string()).nullable(),
  // Additional files
  additionalFiles: z.array(AdditionalFileDTOSchema).nullable(),
  // Review workflow
  reviewStatus: EvidenceReviewStatusSchema,
  reviewedBy: z.string().nullable(),
  reviewedAt: z.date().nullable(),
  comments: z.string().nullable(),
  // Override
  overrideReason: z.string().nullable(),
  overrideUsed: z.boolean(),
  // Ad-hoc conclusion extracted from doc content (for ad-hoc evidence without AI score)
  adHocConclusion: z.string().nullable().optional(),
  // Source execution (for automated evidence)
  sourceExecutionId: z.string().nullable(),
  // Period-based evidence aggregation
  evidenceFromDate: z.date().nullable(),
  evidenceToDate: z.date().nullable(),
  // Rollup statistics (null for single/manual evidence)
  runCount: z.number().nullable(),
  successCount: z.number().nullable(),
  successRate: z.number().nullable(),
  coveredDays: z.number().nullable(),
  gapDays: z.number().nullable(),
  anomalyCount: z.number().nullable(),
  executionIdsJson: z.array(z.string()).nullable(),
  rollupComputedAt: z.date().nullable(),
  // Evidence pack (when generated via submitForReview)
  evidencePack: z.object({
    id: z.string(),
    storageUrl: z.string().nullable(),
    replayVideoUrl: z.string().nullable(),
    topicTitle: z.string().nullable(),
    applicableControls: z.array(z.string()),
  }).nullable().optional(),
  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type EvidenceSubmissionDTO = z.infer<typeof EvidenceSubmissionDTOSchema>;

// RFC-113: Text response for TEXT type required elements
export const TextResponseSchema = z.object({
  requirementId: z.string(),
  value: z.string(),
});
export type TextResponse = z.infer<typeof TextResponseSchema>;

export const CreateEvidenceSubmissionDTOSchema = z.object({
  statusId: z.string(),
  docId: z.string().optional(), // Optional for subtasks that use EvidenceFile instead
  templateVersionId: z.string().optional(), // Optional - will use current template version if not provided
  additionalFiles: z.array(AdditionalFileDTOSchema).optional(),
  // RFC-113: Text responses for TEXT type required elements (e.g., tester conclusions)
  textResponses: z.array(TextResponseSchema).optional(),
  // Guest submission fields
  submittedBy: z.string().optional(), // User ID (for WorkspaceReader guests)
  submittedByEmail: z.string().email().optional(), // Email from share link
  shareToken: z.string().optional(), // Share token used
  // Quality gate override
  overrideValidation: z.boolean().optional(), // User chose to submit despite failing quality gate
  // Source execution (for automated evidence)
  sourceExecutionId: z.string().optional(),
  // Period-based evidence aggregation (optional - null for single/manual)
  evidenceFromDate: z.date().optional(),
  evidenceToDate: z.date().optional(),
});

export type CreateEvidenceSubmissionDTO = z.infer<typeof CreateEvidenceSubmissionDTOSchema>;

export const UpdateEvidenceSubmissionDTOSchema = z.object({
  id: z.string(),
  reviewStatus: EvidenceReviewStatusSchema.optional(),
  comments: z.string().nullable().optional(),
  overrideReason: z.string().nullable().optional(),
  overrideUsed: z.boolean().optional(),
  additionalFiles: z.array(AdditionalFileDTOSchema).optional(),
});

export type UpdateEvidenceSubmissionDTO = z.infer<typeof UpdateEvidenceSubmissionDTOSchema>;

// ===== Template for Recording (sent to extension) =====
export const TemplateForRecordingDTOSchema = z.object({
  templateId: z.string(),
  controlTitle: z.string(),
  controlDescription: z.string(),
  controlId: z.string().optional(), // Control ID (e.g., "AC-1.2.3")
  frameworkName: z.string().optional(), // Framework name (e.g., "SOC 2 Type II")
  instructions: z.string().optional(), // Workflow instructions (markdown) for testers and AI
  requiredElements: z.array(SimplifiedElementSchema),
  qualityThresholds: QualityThresholdsSchema.optional(), // Quality targets
});

export type TemplateForRecordingDTO = z.infer<typeof TemplateForRecordingDTOSchema>;

// ===== Validation Result (LLM structured output) =====
export const ValidationResultSchema = z.object({
  isValid: z.boolean(),
  confidence: z.number().min(0).max(100),
  reasoningText: z.string(),
  detectedElements: z.array(z.string()),
  suggestions: z.array(z.string()).optional(),
});

export type ValidationResult = z.infer<typeof ValidationResultSchema>;

// ===== Task Control DTO (nested control info for TaskDTO) =====
export const TaskControlDTOSchema = z.object({
  id: z.string(),
  controlId: z.string(), // e.g., "CC6.1"
  title: z.string(),
  frameworkId: z.string(),
  frameworkName: z.string().nullable(),
});
export type TaskControlDTO = z.infer<typeof TaskControlDTOSchema>;

// ===== Task DTO (RFC-098: Linear-style task list) =====
// Unified DTO for both "My Tasks", "Review Queue", and "Control Tree" views
// Also used directly by StatusRow component
export const TaskDTOSchema = z.object({
  // From ControlEvidenceStatus (the "task")
  statusId: z.string(),
  assignedTo: z.string().nullable(),
  dueDate: z.date().nullable(),
  externalTicketUrl: z.string().nullable(),
  lastTestedAt: z.date().nullable().optional(), // When test was last run

  // From GuidedTemplate (nullable for ad-hoc recordings)
  templateId: z.string().nullable(),
  templateType: z.enum(['GUIDED', 'STATIC_SCREENSHOT', 'WORKFLOW', 'POLICY', 'API']).nullable().optional(),
  topicId: z.string().nullable(), // For navigation to evidence detail page
  displayId: z.string(), // e.g., "CC6.1-01" or "CC6.1-AH" for ad-hoc
  title: z.string(), // From template.doc.topic.title or "Ad-hoc Evidence"
  isAdHoc: z.boolean(), // True if no template (ad-hoc recording)

  // Controls associated with this task (P1e: multi-control support)
  controls: z.array(TaskControlDTOSchema),

  // Status from ControlEvidenceStatus
  status: ControlEvidenceStatusEnumSchema,

  // From EvidenceSubmission (if exists)
  submission: z.object({
    id: z.string(),
    reviewStatus: EvidenceReviewStatusSchema,
    overallScore: z.number().nullable(),
    qualityTier: z.enum(['EXCELLENT', 'ACCEPTABLE', 'NEEDS_IMPROVEMENT', 'FAIL']).nullable(),
    reviewerComments: z.string().nullable(),
    submittedAt: z.date(),
    revisionNumber: z.number().optional(),
    executionId: z.string().nullable().optional(), // Source execution for navigation
  }).nullable(),

  // Assignee info (for display)
  assignee: z.object({
    id: z.string(),
    name: z.string(),
    image: z.string().nullable(),
  }).nullable(),

  // Submitter info (for review queue - who submitted the evidence)
  submitter: z.object({
    id: z.string(),
    name: z.string(),
    image: z.string().nullable(),
  }).nullable().optional(),

  // Reviewer info (for reviewed submissions)
  reviewer: z.object({
    id: z.string(),
    name: z.string(),
    image: z.string().nullable(),
  }).nullable().optional(),
  reviewedAt: z.date().nullable().optional(),

  // RFC-107: Subtask stats for sampling tests (parent tasks with subtasks)
  subtaskStats: z.object({
    total: z.number(),
    completed: z.number(),
    inProgress: z.number(),
    notStarted: z.number(),
  }).nullable().optional(),

  // RFC-107: Sampling workflow indicator
  // true = has sample with subtasks, false = needs sample/subtasks, null = not a sampling test
  hasSample: z.boolean().nullable().optional(),

  // Test instructions (stored directly in ControlEvidenceStatus)
  // For subtasks: copied from subtaskDef.scoringCriteria at creation
  // For parent tasks: copied from template.scoringCriteria at creation
  instructions: z.string().nullable().optional(),
  requiredElements: z.array(SimplifiedElementSchema).optional(),
  qualityThresholds: QualityThresholdsSchema.nullable().optional(),

  // Labels assigned to this task
  labels: z.array(LabelDTOSchema).optional(),
});

export type TaskDTO = z.infer<typeof TaskDTOSchema>;

// ===== Get My Tasks Input =====
export const TaskSortBySchema = z.enum(['dueDate', 'createdAt']);
export type TaskSortBy = z.infer<typeof TaskSortBySchema>;

export const SortOrderSchema = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof SortOrderSchema>;

export const GetMyTasksInputSchema = z.object({
  status: z.array(ControlEvidenceStatusEnumSchema).optional(),
  frameworkId: z.string().optional(),
  search: z.string().optional(),
  sortBy: TaskSortBySchema.optional().default('dueDate'),
  sortOrder: SortOrderSchema.optional().default('asc'),
  labelIds: z.array(z.string()).optional(),
  assigneeOnly: z.boolean().optional(),
  templateType: TemplateTypeSchema.optional(),
});

export type GetMyTasksInput = z.infer<typeof GetMyTasksInputSchema>;

// ===== Assign Test Input =====
export const AssignTestInputSchema = z.object({
  statusId: z.string(),
  assigneeId: z.string(),
  dueDate: z.date().optional(),
});

export type AssignTestInput = z.infer<typeof AssignTestInputSchema>;

// ===== Update Assignment Input =====
export const UpdateAssignmentInputSchema = z.object({
  statusId: z.string(),
  assigneeId: z.string().nullish(),
  dueDate: z.date().nullish(),
  externalTicketUrl: z.string().nullish(),
  frequency: EvidenceFrequencySchema.nullish(),
});

export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentInputSchema>;

// ===== Bulk Review Input =====
export const BulkReviewInputSchema = z.object({
  submissionIds: z.array(z.string()),
  decision: z.enum(['APPROVED', 'NEEDS_REVISION']),
  comments: z.string().optional(),
});

export type BulkReviewInput = z.infer<typeof BulkReviewInputSchema>;

// ===== Control Tree (RFC-098 Phase 3) =====
export const ControlTreeStatusSchema = z.enum([
  'PASSED',
  'FAILED',
  'NEEDS_REVIEW',
  'NOT_TESTED',
  'IN_PROGRESS',
  'PARTIAL',
  'NOT_APPLICABLE',
]);
export type ControlTreeStatus = z.infer<typeof ControlTreeStatusSchema>;

export const ControlTreeNodeSchema: z.ZodType<ControlTreeNode> = z.lazy(() =>
  z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['framework', 'category', 'control', 'test']),
    status: ControlTreeStatusSchema,
    counts: z.object({
      passed: z.number(),
      failed: z.number(),
      needsReview: z.number(),
      notTested: z.number(),
    }),
    children: z.array(ControlTreeNodeSchema).optional(),
    // Control/test specific
    evidenceCount: z.number().optional(),
    lastTestedAt: z.date().nullable().optional(),
    controlId: z.string().optional(), // For controls: CC6.1
    templateId: z.string().optional(), // For tests
    statusId: z.string().optional(), // For tests: ControlEvidenceStatus ID
    evidenceStatus: ControlEvidenceStatusEnumSchema.optional(), // For tests: Raw database status (single source of truth)
    topicId: z.string().optional(), // For tests: Topic ID for navigation
    taskNumber: z.number().optional(), // For tests: Task number (0 for ad-hoc)
    taskCode: z.string().optional(), // For tests: Workspace-scoped display code (e.g., DEE-1)
    isAdHoc: z.boolean().optional(), // For tests: True if no template
    assignee: z
      .object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string(),
        image: z.string().nullable(),
      })
      .nullable()
      .optional(), // For tests: Assigned user info
    // RFC-107: Subtask stats for sampling tests
    subtaskStats: z.object({
      total: z.number(),
      completed: z.number(),
      inProgress: z.number(),
      notStarted: z.number(),
    }).nullable().optional(),
    // RFC-107: Sampling workflow indicator (POLICY type)
    hasSample: z.boolean().nullable().optional(),
    // Labels for tests
    labels: z.array(LabelDTOSchema).optional(),
  })
);

export interface ControlTreeNode {
  id: string;
  name: string;
  type: 'framework' | 'category' | 'control' | 'test';
  status: ControlTreeStatus;
  counts: {
    passed: number;
    failed: number;
    needsReview: number;
    notTested: number;
  };
  children?: ControlTreeNode[];
  evidenceCount?: number;
  lastTestedAt?: Date | null;
  controlId?: string;
  templateId?: string;
  statusId?: string;
  evidenceStatus?: ControlEvidenceStatusEnum; // For tests: Raw database status (single source of truth)
  topicId?: string; // For tests: Topic ID for navigation
  taskNumber?: number; // For tests: Task number (0 for ad-hoc)
  taskCode?: string; // For tests: Workspace-scoped display code (e.g., DEE-1)
  isAdHoc?: boolean; // For tests: True if no template
  templateType?: 'GUIDED' | 'STATIC_SCREENSHOT' | 'WORKFLOW' | 'POLICY' | 'API'; // For tests: Template type
  assignee?: {
    id: string;
    name: string | null;
    email: string;
    image: string | null;
  } | null;
  // RFC-104: Sample-based evidence (one evidence covers entire sample)
  sample?: {
    id: string;
    sampleSize: number;
    passedCount: number | null;
    failedCount: number | null;
    pendingCount: number | null;
    populationName: string;
    populationType: string;
  } | null;
  // RFC-107: Subtask stats for sampling tests
  subtaskStats?: {
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
  } | null;
  // RFC-107: Sampling workflow indicator (POLICY type)
  hasSample?: boolean | null;
  // Labels for tests
  labels?: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  // Due date for tests
  dueDate?: Date | null;
}

// ===== Get Controls Tree Input =====
export const GetControlsTreeInputSchema = z.object({
  frameworkId: z.string().optional(),
  status: z.array(ControlTreeStatusSchema).optional(),
});

export type GetControlsTreeInput = z.infer<typeof GetControlsTreeInputSchema>;

// ===== Test Share Link DTOs (RFC-099+) =====
export const TestShareLinkDTOSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  controlDefId: z.string().optional(),
  shareToken: z.string(),
  shareAccessType: ShareAccessTypeSchema,
  sharePasswordHash: z.string().optional(),
  shareEmailDomains: z.array(z.string()),
  expiresAt: z.date().optional(),
  createdBy: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type TestShareLinkDTO = z.infer<typeof TestShareLinkDTOSchema>;

export const CreateTestShareLinkDTOSchema = z.object({
  templateId: z.string(),
  controlDefId: z.string().optional(),
  shareAccessType: ShareAccessTypeSchema.optional(),
  password: z.string().optional(), // Plain text password (will be hashed)
  emailDomains: z.array(z.string()).optional(),
  expiresAt: z.date().optional(),
});

export type CreateTestShareLinkDTO = z.infer<typeof CreateTestShareLinkDTOSchema>;

export const UpdateTestShareLinkDTOSchema = z.object({
  id: z.string(),
  shareAccessType: ShareAccessTypeSchema.optional(),
  password: z.string().optional(),
  emailDomains: z.array(z.string()).optional(),
  expiresAt: z.date().optional(),
});

export type UpdateTestShareLinkDTO = z.infer<typeof UpdateTestShareLinkDTOSchema>;

// ===== Shared Test Template DTOs (for public share link pages) =====

/**
 * Control info as returned in shared test context
 */
export const SharedTestControlSchema = z.object({
  id: z.string(),
  controlId: z.string(), // e.g., "CC6.1"
  title: z.string(),
  framework: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
  }).nullable(),
});

export type SharedTestControl = z.infer<typeof SharedTestControlSchema>;

/**
 * Control mapping for a shared test template
 */
export const SharedTestControlMappingSchema = z.object({
  id: z.string(),
  isPrimary: z.boolean(),
  displayOrder: z.number(),
  controlDef: SharedTestControlSchema,
});

export type SharedTestControlMapping = z.infer<typeof SharedTestControlMappingSchema>;

/**
 * Shared test template response from getSharedTest API
 * This is the shape returned when accessing a shared test via share link
 */
export const SharedTestTemplateDTOSchema = z.object({
  // Template core fields
  id: z.string(),
  docId: z.string(),
  frameworkId: z.string(),
  isPublic: z.boolean(),
  usageCount: z.number(),
  currentVersion: z.number(),
  scoringCriteria: ScoringCriteriaSchema.nullable(),
  taskNumber: z.number().nullable().optional(),
  author: z.string().nullable().optional(),
  license: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),

  // RFC-101: Template type (GUIDED, STATIC_SCREENSHOT, WORKFLOW, DOCUMENT, API, POLICY)
  templateType: z.enum(['GUIDED', 'STATIC_SCREENSHOT', 'WORKFLOW', 'DOCUMENT', 'API', 'POLICY']).optional(),

  // Doc with topic info
  doc: z.object({
    id: z.string(),
    topic: z.object({
      id: z.string(),
      title: z.string(),
      workspaceId: z.string(),
    }).nullable(),
  }).nullable(),

  // Framework reference
  framework: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    workspaceId: z.string().nullable(),
  }).nullable(),

  // Control mappings (many-to-many)
  controlMappings: z.array(SharedTestControlMappingSchema).optional(),

  // Context fields added by getSharedTest
  workspaceId: z.string().optional(),
  statusId: z.string().optional(), // Task ID (ControlEvidenceStatus)
  testCode: z.string().optional(), // Generated task code (e.g., "ACM-184")
});

export type SharedTestTemplateDTO = z.infer<typeof SharedTestTemplateDTOSchema>;

// Helper type for display title extraction
export function getSharedTestTitle(template: SharedTestTemplateDTO): string {
  return template.doc?.topic?.title || 'Untitled Test';
}

// Helper to get controls for display
export function getSharedTestControls(template: SharedTestTemplateDTO): SharedTestControl[] {
  return template.controlMappings?.map(m => m.controlDef) || [];
}

// ===== RFC-107: Subtask DTOs for Per-Item Sampling Evidence =====

/**
 * Create subtasks from sample - input DTO
 */
export const CreateSubtasksFromSampleInputSchema = z.object({
  /** Parent test ID (ControlEvidenceStatus) */
  parentId: z.string(),
  /** Sample ID to generate subtasks from */
  sampleId: z.string(),
  /** Optional: Create subtasks only for specific sample item IDs */
  sampleItemIds: z.array(z.string()).optional(),
});

export type CreateSubtasksFromSampleInput = z.infer<typeof CreateSubtasksFromSampleInputSchema>;

/**
 * Subtask generation result
 */
export const SubtaskGenerationResultDTOSchema = z.object({
  /** Number of subtasks created */
  createdCount: z.number(),
  /** IDs of created subtasks */
  subtaskIds: z.array(z.string()),
  /** Any errors during creation */
  errors: z.array(z.object({
    sampleItemId: z.string(),
    message: z.string(),
  })).optional(),
});

export type SubtaskGenerationResultDTO = z.infer<typeof SubtaskGenerationResultDTOSchema>;

/**
 * Parent test with subtasks - full DTO for detail view
 */
export const TestWithSubtasksDTOSchema = ControlEvidenceStatusDTOSchema.extend({
  // Subtasks are returned as TaskDTO[] for direct use with StatusRow component
  subTasks: z.array(TaskDTOSchema).optional(),
  subTaskStats: z.object({
    total: z.number(),
    completed: z.number(),
    inProgress: z.number(),
    notStarted: z.number(),
    approved: z.number(),
    needsRevision: z.number(),
  }).optional(),
});

export type TestWithSubtasksDTO = z.infer<typeof TestWithSubtasksDTOSchema>;

/**
 * Status rollup result - computed parent status based on subtasks
 */
export const StatusRollupResultSchema = z.object({
  computedStatus: ControlEvidenceStatusEnumSchema,
  stats: z.object({
    total: z.number(),
    byStatus: z.record(z.number()),
  }),
  /** Whether parent status should be updated */
  shouldUpdate: z.boolean(),
});

export type StatusRollupResult = z.infer<typeof StatusRollupResultSchema>;

/**
 * Compute parent status from subtask statuses
 * This is a pure function for status rollup logic
 *
 * ControlEvidenceStatusEnum values:
 * - NOT_STARTED, IN_PROGRESS, SUBMITTED, NEEDS_RETEST, APPROVED, NOT_APPLICABLE, NON_PERFORMING
 */
export function computeParentStatusFromSubtasks(
  subtaskStatuses: Array<{ status: string }>
): StatusRollupResult {
  const total = subtaskStatuses.length;

  if (total === 0) {
    return {
      computedStatus: 'NOT_STARTED',
      stats: { total: 0, byStatus: {} },
      shouldUpdate: false,
    };
  }

  // Count by status
  const byStatus: Record<string, number> = {};
  for (const { status } of subtaskStatuses) {
    byStatus[status] = (byStatus[status] || 0) + 1;
  }

  // Rollup logic using actual ControlEvidenceStatusEnum values:
  // - All APPROVED → APPROVED
  // - Any NEEDS_RETEST → NEEDS_RETEST (needs attention)
  // - Any IN_PROGRESS or SUBMITTED → IN_PROGRESS
  // - Otherwise → NOT_STARTED

  let computedStatus: string;

  if (byStatus['APPROVED'] === total) {
    computedStatus = 'APPROVED';
  } else if (byStatus['NEEDS_RETEST']) {
    computedStatus = 'NEEDS_RETEST';
  } else if (byStatus['IN_PROGRESS'] || byStatus['SUBMITTED']) {
    computedStatus = 'IN_PROGRESS';
  } else {
    computedStatus = 'NOT_STARTED';
  }

  return {
    computedStatus: computedStatus as any,
    stats: { total, byStatus },
    shouldUpdate: true,
  };
}

// ===== RFC-091 Extension: Partner Template Sharing DTOs =====

/** Visibility levels for templates */
export const TemplateVisibilitySchema = z.enum(['PUBLIC', 'ORGANIZATION', 'PARTNER_CLIENTS', 'WORKSPACE']);
export type TemplateVisibility = z.infer<typeof TemplateVisibilitySchema>;

/** Input for sharing a template with partner clients */
export const ShareTemplateWithClientsInputSchema = z.object({
  templateId: z.string(),
  /** Client org IDs to share with. Omit or empty = all clients */
  clientOrgIds: z.array(z.string()).optional(),
  /** Allow clients to clone this template */
  clientsCanClone: z.boolean().default(true),
  /** Allow clients to view the source doc content */
  clientsCanViewSource: z.boolean().default(false),
});
export type ShareTemplateWithClientsInput = z.infer<typeof ShareTemplateWithClientsInputSchema>;

/** Input for updating template sharing settings */
export const UpdateTemplateSharingInputSchema = z.object({
  templateId: z.string(),
  clientOrgIds: z.array(z.string()).optional(),
  clientsCanClone: z.boolean().optional(),
  clientsCanViewSource: z.boolean().optional(),
});
export type UpdateTemplateSharingInput = z.infer<typeof UpdateTemplateSharingInputSchema>;

/** Shared template info for partner dashboard */
export const PartnerSharedTemplateDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  title: z.string(),
  framework: z.object({
    id: z.string(),
    name: z.string(),
    shortName: z.string().optional(),
  }).nullable(),
  controls: z.array(z.object({
    id: z.string(),
    controlId: z.string(),
    title: z.string(),
  })),
  /** True if shared with all clients */
  sharedWithAll: z.boolean(),
  /** Specific clients this is shared with */
  sharedWithClients: z.array(z.object({
    id: z.string(),
    name: z.string(),
  })),
  clientsCanClone: z.boolean(),
  clientsCanViewSource: z.boolean(),
  cloneCount: z.number(),
  updatedAt: z.date(),
});
export type PartnerSharedTemplateDTO = z.infer<typeof PartnerSharedTemplateDTOSchema>;

/** Partner-shared template info for client view */
export const ClientPartnerTemplateDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  title: z.string(),
  /** Content (only if clientsCanViewSource is true) */
  content: z.unknown().nullable(),
  partnerOrg: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable(),
  framework: z.object({
    id: z.string(),
    name: z.string(),
    shortName: z.string().optional(),
  }).nullable(),
  controls: z.array(z.object({
    id: z.string(),
    controlId: z.string(),
    title: z.string(),
  })),
  canClone: z.boolean(),
  canViewSource: z.boolean(),
  templateType: TemplateTypeSchema,
  scoringCriteria: ScoringCriteriaSchema.nullable(),
  executionConfig: TemplateExecutionConfigSchema.nullable().optional(),
  samplingConfig: TemplateSamplingConfigSchema.nullable().optional(),
  updatedAt: z.date(),
});
export type ClientPartnerTemplateDTO = z.infer<typeof ClientPartnerTemplateDTOSchema>;

/** Response for listPartnerSharedTemplates (client-side) */
export const ListPartnerSharedTemplatesResponseSchema = z.object({
  templates: z.array(ClientPartnerTemplateDTOSchema),
  total: z.number(),
  hasPartner: z.boolean(),
  partnerName: z.string().optional(),
});
export type ListPartnerSharedTemplatesResponse = z.infer<typeof ListPartnerSharedTemplatesResponseSchema>;
